// Deep merges translation objects so that `custom` can override individual
// leaf keys (e.g. an org-tweaked `eventPage.start_date`) without nuking the
// whole section of `common` that lives at the same path. The previous shallow
// spread `{ ...common, ...custom }` was wiping out e.g. every classification
// key in `pt` and `en` because their `custom.json` ships a partial
// `eventPage` block that just tweaks 9 / 2 translations.
//
// Arrays are replaced wholesale (we never need to merge two arrays in this
// codebase); objects are merged recursively; everything else is replaced.

type Plain = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Plain =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Generic types in the variadic signature would force every caller to specify
// the union of all source shapes; we instead return the union of the inputs so
// the result keeps the leaves of both common and custom (custom values win).
export function deepMerge<A, B>(a: A, b: B): A & B {
  const out: Plain = {};
  for (const src of [a as unknown as Plain, b as unknown as Plain]) {
    if (!src) continue;
    for (const key of Object.keys(src)) {
      const next = src[key];
      const prev = out[key];
      if (isPlainObject(prev) && isPlainObject(next)) {
        out[key] = deepMerge(prev, next);
      } else {
        out[key] = next;
      }
    }
  }
  return out as A & B;
}
